import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Clock, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { COUNTRIES, CONTACT } from "../i18n/translations";
import Reveal from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = { full_name: "", phone: "", email: "", country: "", service: "", message: "" };

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success_t, { description: t.contact.success_d });
      setForm(initialState);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(t.contact.error_t, {
        description: Array.isArray(detail) ? detail[0]?.msg || t.contact.error_d : detail || t.contact.error_d,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32">
      {/* Hero */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#D4AF37]">{t.contact.overline}</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0A192F] tracking-tight leading-[1.05]">
              {t.contact.title}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-gray-600 text-lg leading-relaxed max-w-2xl">{t.contact.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-white border border-gray-200 p-8 sm:p-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label={t.contact.labels.name} name="full_name" value={form.full_name} onChange={onChange} placeholder={t.contact.placeholders.name} testid="cf-name" required />
                  <Field label={t.contact.labels.phone} name="phone" value={form.phone} onChange={onChange} placeholder={t.contact.placeholders.phone} testid="cf-phone" required />
                  <Field label={t.contact.labels.email} name="email" type="email" value={form.email} onChange={onChange} placeholder={t.contact.placeholders.email} testid="cf-email" required />
                  <SelectField label={t.contact.labels.country} name="country" value={form.country} onChange={onChange} placeholder={t.contact.placeholders.country} options={COUNTRIES} testid="cf-country" required />
                  <SelectField className="sm:col-span-2" label={t.contact.labels.service} name="service" value={form.service} onChange={onChange} placeholder={t.contact.placeholders.service} options={t.contact.services_options} testid="cf-service" required />
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A192F]">{t.contact.labels.message}</label>
                    <textarea
                      data-testid="cf-message"
                      name="message"
                      required
                      minLength={5}
                      rows={5}
                      value={form.message}
                      onChange={onChange}
                      placeholder={t.contact.placeholders.message}
                      className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-sm text-[#0A192F] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting}
                  className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors disabled:opacity-60"
                >
                  {submitting ? "..." : t.contact.submit} <ArrowUpRight className="w-4 h-4" />
                </button>
              </form>
            </Reveal>
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal delay={0.05}>
              <div className="bg-[#0A192F] text-white p-8 sm:p-10">
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">{t.contact.info_t}</p>
                <h3 className="mt-3 font-serif text-2xl sm:text-3xl">Mercurio Advisors Group</h3>

                <ul className="mt-7 space-y-5 text-sm">
                  <InfoRow icon={<MapPin className="w-4 h-4" />} title={t.contact.address_t} value={CONTACT.address} />
                  <InfoRow icon={<Phone className="w-4 h-4" />} title="Phone" value={<a href={CONTACT.whatsapp_link} target="_blank" rel="noopener noreferrer" data-testid="info-phone" className="hover:text-[#D4AF37]">{CONTACT.phone}</a>} />
                  <InfoRow icon={<MessageCircle className="w-4 h-4" />} title="WhatsApp" value={<a href={CONTACT.whatsapp_link} data-testid="info-whatsapp" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37]">{CONTACT.whatsapp}</a>} />
                  <InfoRow icon={<Mail className="w-4 h-4" />} title="Email" value={<a href={`mailto:${CONTACT.email}`} data-testid="info-email" className="hover:text-[#D4AF37]">{CONTACT.email}</a>} />
                  <InfoRow icon={<Clock className="w-4 h-4" />} title={t.contact.hours_t} value={t.contact.hours_d} />
                </ul>

                <div className="mt-8 border-t border-white/15 pt-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Division Emails</p>
                  <ul className="space-y-2 text-sm text-gray-300">
                    {CONTACT.emails_extra.map((e) => (
                      <li key={e}><a className="hover:text-white" href={`mailto:${e}`}>{e}</a></li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-6 border border-gray-200 bg-white">
                <iframe
                  data-testid="contact-map"
                  title="Valencia Map"
                  src={CONTACT.maps_embed}
                  className="w-full h-72 border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, placeholder, type = "text", testid, required }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A192F]">{label}</label>
    <input
      data-testid={testid}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-sm text-[#0A192F] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, placeholder, options, testid, required, className = "" }) => (
  <div className={className}>
    <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A192F]">{label}</label>
    <select
      data-testid={testid}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-sm text-[#0A192F] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
    >
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const InfoRow = ({ icon, title, value }) => (
  <li className="flex items-start gap-4">
    <span className="inline-flex items-center justify-center w-8 h-8 border border-white/20 text-[#D4AF37] shrink-0">{icon}</span>
    <div>
      <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400">{title}</p>
      <p className="mt-1 text-gray-100">{value}</p>
    </div>
  </li>
);

export default Contact;
